import { OG_IMAGE } from '@shared/constants/seo';
import React, { useMemo } from 'react';

import type { HelmetProps } from 'react-helmet-async';
import { Helmet } from 'react-helmet-async';
import { I18N_FALLBACK_LOCALE, SUPPORTED_LOCALES } from '@withjoy/locale';
import { localeUrl } from '@shared/utils/localeUrl';

interface SeoProps extends Readonly<{}> {
  description?: string;
  lang?: string;
  title: string;
  name?: string;
  author?: string;
  email?: string;
  url?: string;
  fbAppId?: string;
  keywords?: string;
  disableIndexing?: boolean;
  canonicalURL?: string | null;
  /**
   * The page is served translated in every supported locale through `?l=<locale>`: renders hreflang
   * alternates for every locale plus x-default. Pass the current locale's URL as `canonicalURL`
   * (`useLocalizedUrl`). Leave it off for pages that only exist in English.
   */
  localized?: boolean;
  ogImage?: string;
  ogLogo?: string;
  twitterSite?: string;
  ogType?: string;
}

const useSeo = (props: SeoProps) => {
  const metaTags = useMemo<HelmetProps['meta']>(() => {
    const { name, url, author, title, description, ogImage = OG_IMAGE, ogLogo, ogType = 'website', twitterSite } = props;
    return [
      // Enable once public shop goes live
      // {
      //   name: 'robots',
      //   content: disableIndexing ? 'noindex' : 'index'
      // },
      {
        name: `description`,
        content: description,
      },
      {
        name: `author`,
        content: author,
      },

      // Open Graph

      {
        property: `og:site_name`,
        content: name,
      },
      {
        property: `og:url`,
        content: url,
      },
      {
        property: `og:title`,
        content: title,
      },
      {
        property: `og:description`,
        content: description,
      },
      {
        property: `og:image`,
        content: ogImage,
      },
      {
        property: `og:logo`,
        content: ogLogo,
      },
      {
        property: `og:type`,
        content: ogType,
      },

      // Twitter
      {
        name: `twitter:card`,
        content: `summary`,
      },
      {
        name: `twitter:creator`,
        content: author,
      },
      {
        name: `twitter:title`,
        content: title,
      },
      {
        name: `twitter:description`,
        content: description,
      },
      {
        name: `twitter:url`,
        content: url,
      },
      {
        name: `twitter:site`,
        content: twitterSite,
      },
    ].filter(({ content }) => !!content);
  }, [props]);

  return {
    metaTags,
  };
};

export const Seo: React.FC<SeoProps> = props => {
  const { metaTags } = useSeo(props);
  const { canonicalURL, localized } = props;
  const alternates = useMemo(
    () =>
      canonicalURL && localized
        ? [
            ...SUPPORTED_LOCALES.map(locale => ({ hrefLang: locale, href: localeUrl(canonicalURL, locale) })),
            { hrefLang: 'x-default', href: localeUrl(canonicalURL, I18N_FALLBACK_LOCALE) },
          ]
        : [],
    [canonicalURL, localized],
  );

  return (
    <Helmet
      title={props.title}
      meta={metaTags}
    >
      {canonicalURL && (
        <link
          rel="canonical"
          href={canonicalURL}
        />
      )}
      {alternates.map(({ hrefLang, href }) => (
        <link
          key={hrefLang}
          rel="alternate"
          hrefLang={hrefLang}
          href={href}
        />
      ))}
    </Helmet>
  );
};

Seo.defaultProps = {
  author: 'Joy',
  disableIndexing: false,
};
